import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useAtom } from 'jotai';
import {
  bindMenu,
  bindTrigger,
  usePopupState
} from 'material-ui-popup-state/hooks';
import { useRef, useState } from 'react';

import { AUTO_SOC_TIERS } from '@/constants/products.constants';
import TicketStatusDialogService from '@/features/TicketStatusDialogService';
import TicketStatusDialogThreat from '@/features/TicketStatusDialogThreat';
import useStaticData from '@/hooks/StaticData.hooks';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import {
  TICKET_STATUS_BUTTON_ACKNOWLEDGE,
  TICKET_STATUS_BUTTON_CLOSE,
  TICKET_STATUS_BUTTON_NONE,
  TICKET_STATUS_BUTTON_PUBLISH,
  TICKET_STATUS_BUTTON_REMEDIATE,
  TICKET_STATUS_BUTTON_REOPEN,
  TICKET_STATUS_BUTTON_START_REVIEW
} from '@/pages/Ticket/Ticket.constants';
import {
  ticketAtom,
  ticketErrorAtom,
  ticketWatchersAtom,
  useUpdateStatus,
  watchersPageOptionsAtom
} from '@/pages/Ticket/Ticket.store';
import {
  TICKET_STATUS,
  TICKET_STATUS_ID
} from '@/pages/Tickets/Tickets.constants';
import { isLayoutMobileAtom } from '@/store/layout';
import { userAtom, userIsInternalAtom } from '@/store/user.store';

import type ButtonAttributes from './TicketDetailsStatusButton.types';

const TicketDetailsStatusButton = () => {
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const [{ ticket_status, ticket_type, user, organization }] =
    useAtom(ticketAtom);
  const [userData] = useAtom(userAtom);
  const [userIsInternal] = useAtom(userIsInternalAtom);
  const [{ id: selectedOrgId }] = useAtom(selectedOrganizationAtom);
  const popupState = usePopupState({
    popupId: 'ticketStatusMenu',
    variant: 'popover'
  });
  const reopenButtonAnchorRef = useRef<HTMLButtonElement>(null);
  const [isTicketStatusDialogServiceOpen, setIsTicketStatusDialogServiceOpen] =
    useState(false);
  const [isTicketStatusDialogThreatOpen, setIsTicketStatusDialogThreatOpen] =
    useState(false);
  const isAutoSocOrg = organization?.is_auto_soc > AUTO_SOC_TIERS.FORTIFY;

  const { statuses } = useStaticData();
  const [ticketWatchers] = useAtom(ticketWatchersAtom);
  const [watcherOptions] = useAtom(watchersPageOptionsAtom);
  const [, setTicketError] = useAtom(ticketErrorAtom);

  const { updateStatus } = useUpdateStatus();

  const ticket_status_name = ticket_status?.name as TICKET_STATUS;

  const handleClick = () => {
    // threat tickets (remediate or close statuses)
    if (
      (ticket_type === 1 && newStatus?.name === TICKET_STATUS.REMEDIATED) ||
      (ticket_type === 1 && newStatus?.name === TICKET_STATUS.COMPLETED)
    ) {
      setIsTicketStatusDialogThreatOpen(true);
      return;
    }

    // service tickets (close status)
    if (ticket_type === 2 && newStatus?.name === TICKET_STATUS.COMPLETED) {
      setIsTicketStatusDialogServiceOpen(true);
      return;
    }

    // all other statuses
    if (newStatus) {
      if (newStatus.name === TICKET_STATUS.OPEN) {
        let containsOrgUser = false;
        ticketWatchers.forEach((watcher) => {
          const userObject = watcherOptions.find(
            (watcherOption) => watcherOption.id === watcher.userId
          );
          if (
            (userObject && !userObject?.internal) ||
            (userObject?.internal && selectedOrgId === 1)
          ) {
            containsOrgUser = true;
          }
        });
        if (!containsOrgUser) {
          setTicketError(
            'Watchers list must contain users from the current organization'
          );
          return;
        }
      }
      updateStatus({ ticket_status_id: newStatus.id });
    }
  };

  const getAcknowledgedButton = (): ButtonAttributes => {
    // non-internal users (specifically for threat tickets)
    if (ticket_type === 1 && !userIsInternal && !isAutoSocOrg) {
      return TICKET_STATUS_BUTTON_REMEDIATE;
    }
    // non-internal users & internal users
    return TICKET_STATUS_BUTTON_CLOSE;
  };

  const getOpenButton = (): ButtonAttributes => {
    // threat tickets
    if (ticket_type === 1) {
      if (userIsInternal) {
        // internal users
        return TICKET_STATUS_BUTTON_CLOSE;
      }
      // non-internal users
      return TICKET_STATUS_BUTTON_ACKNOWLEDGE;
    }

    // service tickets
    if (ticket_type === 2) {
      /**
       * rules:
       *  - a user can close their own ticket
       *  - a user cannot acknowledge their own ticket
       *  - an internal user can acknowledge an internal user's ticket (so long as they are not the author)
       *  - a non-internal user cannot acknowledge a non-internal user's ticket
       */
      if (
        userIsInternal !== user?.internal ||
        (userIsInternal && userData?.email !== user?.email)
      ) {
        return TICKET_STATUS_BUTTON_ACKNOWLEDGE;
      }
    }
    return TICKET_STATUS_BUTTON_NONE;
  };

  const handleReopenStatus = (selection: string) => {
    popupState.close();
    if (selection === 'Open') {
      updateStatus({ ticket_status_id: parseInt(TICKET_STATUS_ID.OPEN, 10) });
      return;
    }
    if (selection === 'Acknowledged') {
      updateStatus({
        ticket_status_id: parseInt(TICKET_STATUS_ID.ACKNOWLEDGED, 10)
      });
      return;
    }
    if (selection === 'Remediated') {
      updateStatus({
        ticket_status_id: parseInt(TICKET_STATUS_ID.REMEDIATED, 10)
      });
    }
  };

  const getButton = (): ButtonAttributes => {
    if (userIsInternal) {
      // internal users
      // service tickets & threat tickets
      switch (ticket_status_name) {
        case TICKET_STATUS.DRAFT:
          return TICKET_STATUS_BUTTON_START_REVIEW;
        case TICKET_STATUS.NEEDS_REVIEW:
          return TICKET_STATUS_BUTTON_PUBLISH;
        case TICKET_STATUS.OPEN:
          return getOpenButton();
        case TICKET_STATUS.ACKNOWLEDGED:
          return getAcknowledgedButton();
        case TICKET_STATUS.REMEDIATED:
          return TICKET_STATUS_BUTTON_CLOSE;
        case TICKET_STATUS.COMPLETED:
          // only internal admin users can reopen tickets
          return userData?.roles.includes('admin')
            ? TICKET_STATUS_BUTTON_REOPEN
            : TICKET_STATUS_BUTTON_NONE;
        case TICKET_STATUS.AUTO_CLOSED:
          // only internal admin users can reopen tickets
          return userData?.roles.includes('admin')
            ? TICKET_STATUS_BUTTON_REOPEN
            : TICKET_STATUS_BUTTON_NONE;
        default:
          return TICKET_STATUS_BUTTON_NONE;
      }
    } else {
      // non-internal users
      // threat tickets
      if (ticket_type === 1) {
        switch (ticket_status_name) {
          case TICKET_STATUS.OPEN:
            return getOpenButton();
          case TICKET_STATUS.ACKNOWLEDGED:
            return getAcknowledgedButton();
          case TICKET_STATUS.REMEDIATED:
            return TICKET_STATUS_BUTTON_CLOSE;
          default:
        }
      }
      // service tickets
      if (ticket_type === 2) {
        switch (ticket_status_name) {
          case TICKET_STATUS.OPEN:
            return getOpenButton();
          case TICKET_STATUS.ACKNOWLEDGED:
            return getAcknowledgedButton();
          case TICKET_STATUS.REMEDIATED:
            return TICKET_STATUS_BUTTON_CLOSE;
          default:
        }
      }
    }
    return TICKET_STATUS_BUTTON_NONE;
  };

  const button = getButton();
  const newStatus = statuses.find(
    (status) => status.name === button.newStatusName
  );

  // no label, don't render button
  if (!button.label) {
    return null;
  }

  // completed ticket status (reopen button)
  if (
    ticket_status_name === TICKET_STATUS.COMPLETED ||
    ticket_status_name === TICKET_STATUS.AUTO_CLOSED
  ) {
    return (
      <>
        <ButtonGroup
          variant="contained"
          {...(isLayoutMobile && { sx: { width: '100%' } })}
        >
          <Button
            ref={reopenButtonAnchorRef}
            sx={{ width: '100%' }}
            {...bindTrigger(popupState)}
          >
            {button.label}
          </Button>
          <Button size="small" {...bindTrigger(popupState)}>
            <FontAwesomeIcon icon={faCaretDown} size="1x" />
          </Button>
        </ButtonGroup>
        <Menu
          {...bindMenu(popupState)}
          anchorEl={reopenButtonAnchorRef.current}
        >
          <MenuItem onClick={() => handleReopenStatus('Open')}>
            <Typography variant="subtitle2">
              to Open (customer must acknowledge)
            </Typography>
          </MenuItem>
          <MenuItem onClick={() => handleReopenStatus('Acknowledged')}>
            <Typography variant="subtitle2">to Acknowledged</Typography>
          </MenuItem>
          {ticket_type === 1 && (
            <MenuItem onClick={() => handleReopenStatus('Remediated')}>
              <Typography variant="subtitle2">to Remediated</Typography>
            </MenuItem>
          )}
        </Menu>
      </>
    );
  }

  // all other ticket statuses
  return (
    <>
      <Button
        color="primary"
        variant="contained"
        sx={{ whiteSpace: 'nowrap' }}
        onClick={handleClick}
        {...(isLayoutMobile && { fullWidth: true })}
      >
        {button.label}
      </Button>
      {isTicketStatusDialogServiceOpen && newStatus !== undefined && (
        <TicketStatusDialogService
          newStatus={newStatus}
          open={isTicketStatusDialogServiceOpen}
          setOpen={setIsTicketStatusDialogServiceOpen}
        />
      )}
      {isTicketStatusDialogThreatOpen && newStatus !== undefined && (
        <TicketStatusDialogThreat
          open={isTicketStatusDialogThreatOpen}
          setOpen={setIsTicketStatusDialogThreatOpen}
        />
      )}
    </>
  );
};

export default TicketDetailsStatusButton;
